/**
 * Formatting and grouping for the calls view. Pure, so the rules are testable without
 * rendering — money and durations shown next to a charge need to be right.
 */
import type { CallOutcome, CallSummary } from "./api.js";
import type { Order } from "./orders-model.js";

/** Cents → money. The API talks in minor units so no float ever reaches an invoice. */
export function formatMinor(minor: number): string {
  const sign = minor < 0 ? "-" : "";
  return `${sign}$${(Math.abs(minor) / 100).toFixed(2)}`;
}

/** Actual call length, distinct from the rounded-up minutes that were billed. */
export function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
}

/**
 * Staff time saved: how long the agent spent on the phone so staff did not have to. The actual length of every
 * call, not the billed time, which rounds each call up. FAILED calls are left out: they failed on our side, and
 * the agent helped nobody on them.
 */
export function staffTimeSavedSeconds(calls: readonly { durationSeconds: number; outcome: CallOutcome }[]): number {
  return calls.reduce((n, c) => (c.outcome === "FAILED" ? n : n + Math.max(0, c.durationSeconds)), 0);
}

/** A total of call time: "45s", "12m 30s", "3h 5m". */
export function formatTotalTime(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  if (s < 3600) return formatDuration(s);
  const minutes = Math.round(s / 60);
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

/** Billed time as m:ss, e.g. 75 → "1:15". Billing works in 15-second steps, so seconds are always shown. */
export function formatClock(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** The billing rule in one line, from the API's own numbers: "$0.05 per 15 s, rounded up". */
export function billingRuleText(r: { incrementSeconds: number; ratePerIncrementMinor: number }): string {
  return `${formatMinor(r.ratePerIncrementMinor)} per ${r.incrementSeconds} s, rounded up`;
}

/** Staff-facing wording; the API's enum names don't belong on screen. */
const OUTCOME_LABELS: Record<CallOutcome, string> = {
  ORDER_PLACED: "Ordered",
  RESERVED: "Booked a table",
  ABANDONED: "No order",
  TRANSFERRED: "Transferred",
  FAILED: "Failed"
};

export function outcomeLabel(outcome: CallOutcome): string {
  return OUTCOME_LABELS[outcome] ?? outcome;
}

/**
 * Whether an outcome reads as good, neutral or bad.
 * FAILED is ours, not theirs — and it isn't billed — so it reads as a problem, not a
 * disappointing customer.
 */
export function outcomeTone(outcome: CallOutcome): "good" | "neutral" | "bad" {
  if (outcome === "ORDER_PLACED" || outcome === "RESERVED") return "good";
  if (outcome === "FAILED") return "bad";
  return "neutral";
}

/** "2026-08" → "August 2026", for a month picker. */
export function monthLabel(month: string): string {
  const [y, m] = month.split("-");
  const date = new Date(Date.UTC(Number(y), Number(m) - 1, 1));
  if (Number.isNaN(date.getTime())) return month;
  return date.toLocaleDateString(undefined, { month: "long", year: "numeric", timeZone: "UTC" });
}

/** The current month plus the previous n-1, newest first. */
export function recentMonths(count = 6, now = new Date()): string[] {
  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    out.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`);
  }
  return out;
}

export function currentMonth(now = new Date()): string {
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}`;
}

/** Local date+time for a call, since staff think in wall-clock terms. */
export function formatStartedAt(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
}

/** Calls grouped by local calendar day, preserving the newest-first order. */
export function groupByDay<T extends CallSummary>(
  calls: readonly T[]
): { day: string; calls: T[] }[] {
  const groups: { day: string; calls: T[] }[] = [];
  for (const call of calls) {
    const t = Date.parse(call.startedAtIso);
    const day = Number.isNaN(t)
      ? "Unknown date"
      : new Date(t).toLocaleDateString(undefined, {
          weekday: "short",
          month: "short",
          day: "numeric"
        });
    const last = groups[groups.length - 1];
    if (last && last.day === day) last.calls.push(call);
    else groups.push({ day, calls: [call] });
  }
  return groups;
}

/**
 * Join calls to the orders they produced.
 *
 * The ORDER is the single source of truth for what it was worth; a call record only carries the
 * order's id. Billing used to store a copy of the value, which meant the same number lived in
 * two tables and could disagree the moment an order was amended.
 *
 * Both sides are already fetched for this page, so the join costs no extra request.
 */
export interface CallWithOrder extends CallSummary {
  /**
   * The whole order, not just its total: "what did this call cost and what did it produce" is
   * only half the question — the other half is what was actually ordered.
   */
  order?: Order;
}

/** Total in cents, when the order is known. */
export function orderValueMinor(call: CallWithOrder): number | undefined {
  return call.order?.total.amountMinor;
}

export function joinOrders(
  calls: readonly CallSummary[],
  orders: readonly Order[]
): CallWithOrder[] {
  const byId = new Map(orders.map((o) => [o.orderId, o]));
  return calls.map((c) => {
    const order = c.orderId ? byId.get(c.orderId) : undefined;
    return order ? { ...c, order } : { ...c };
  });
}

export interface RevenueSummary {
  /** Total value of the orders these calls produced, cents. */
  orderRevenueMinor: number;
  /** How many orders we could actually value — the rest are unknown, not zero. */
  ordersValued: number;
  /** Revenue per unit of call spend, e.g. 214.9. Undefined when nothing was charged. */
  returnMultiple?: number;
}

export function revenueFor(
  calls: readonly CallWithOrder[],
  billedMinor: number
): RevenueSummary {
  const valued = calls.filter((c) => c.order !== undefined);
  const orderRevenueMinor = valued.reduce((n, c) => n + (orderValueMinor(c) ?? 0), 0);
  const summary: RevenueSummary = { orderRevenueMinor, ordersValued: valued.length };
  // Dividing by zero charges isn't an infinite return.
  if (billedMinor > 0) {
    summary.returnMultiple = Number((orderRevenueMinor / billedMinor).toFixed(1));
  }
  return summary;
}
